import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Container,
  Group,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useLocalStorage, useMediaQuery } from '@mantine/hooks';
import {
  IconCards,
  IconHome,
  IconMeeple,
  IconChevronLeft,
  IconChevronRight,
  IconMail,
  IconSchool,
  IconSettings,
} from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';
import { NavLink as RouterNavLink, Outlet, ScrollRestoration, useLocation, useMatches } from 'react-router-dom';
import { APP_NAME, SUPPORT_EMAIL } from '@creativitas/shared';
import { BRAND } from '@/lib/assets';
import { SESSION_SURFACE_WIDTH } from '@/theme/tokens';
import { useSession } from '@/features/auth/useSession';
import { ADMIN_SECTIONS, isPathWithin } from '@/features/admin/admin-sections';
import { UserMenu } from './UserMenu';

/**
 * Main navigation (§15). One level deep: only the admin section expands, into
 * six areas (§48). Pages inside an area are tabs above the page, not a third
 * menu level — see `features/admin/admin-sections.ts`.
 *
 * Deviation from §15 at the owner's request: «Профиль» is not repeated here —
 * it lives in the user menu at the top right, next to the account it belongs to.
 */
type NavItem = {
  label: string;
  to: string;
  icon: Icon;
  adminOnly?: boolean;
  /** `match` — every address the child stands for; by default its own `to`. */
  children?: { label: string; to: string; match?: string[] }[];
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Главная', to: '/dashboard', icon: IconHome },
  { label: 'МАК-сессии', to: '/spreads', icon: IconCards },
  { label: 'Игры', to: '/games', icon: IconMeeple },
  { label: 'Обучение', to: '/training', icon: IconSchool },
  {
    label: 'Администрирование',
    to: '/admin',
    icon: IconSettings,
    adminOnly: true,
    /* Раздел ведёт на свою первую страницу и подсвечен на любой из своих. */
    children: ADMIN_SECTIONS.map((section) => ({
      label: section.label,
      to: section.pages[0]!.to,
      match: section.pages.map((page) => page.to),
    })),
  },
];

const NAV_WIDTH_FULL = 272;
const NAV_WIDTH_COMPACT = 68;

/** A route opts into the wider working surface with `handle: { wide: true }`. */
type RouteHandle = { wide?: boolean };

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const location = useLocation();
  const wide = useMatches().some((match) => (match.handle as RouteHandle | undefined)?.wide);
  const { user, isAdmin } = useSession();

  /**
   * Collapsing is a per-person preference about their own window, so it is
   * remembered locally rather than on the server. A private window that cannot
   * store it simply starts expanded.
   */
  const [collapsed, setCollapsed] = useLocalStorage({
    key: 'creativitas:nav-collapsed',
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const desktop = useMediaQuery('(min-width: 48em)', false, { getInitialValueInEffect: false });
  const compact = collapsed && desktop;

  // The admin section is only offered to admins; the server enforces it (§64).
  const items = NAV_ITEMS.filter((item) => Boolean(user) && (!item.adminOnly || isAdmin));

  /**
   * Compact mode shows icons only, so each one states its own name (§58).
   *
   * The label element has to be removed, not just emptied: Mantine keeps the
   * body flexing and the icon would sit off to the left of its pill.
   */
  const navLinkStyles = (active: boolean) => ({
    root: {
      borderRadius: 'var(--radius-control)',
      ...(compact ? { justifyContent: 'center', paddingInline: 0 } : {}),
    },
    body: compact ? { display: 'none' } : {},
    section: compact ? { marginInlineEnd: 0 } : {},
    label: { fontWeight: active ? 600 : 500 },
  });

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{
        width: { base: NAV_WIDTH_FULL, sm: collapsed ? NAV_WIDTH_COMPACT : NAV_WIDTH_FULL },
        breakpoint: 'sm',
        // On a phone the navbar is a drawer driven by the burger, never compact.
        collapsed: { mobile: !opened },
      }}
      padding={0}
      styles={{
        header: {
          background: 'var(--surface-card)',
          borderColor: 'var(--border-subtle)',
        },
        navbar: {
          background: 'var(--surface-card)',
          borderColor: 'var(--border-subtle)',
        },
        main: { background: 'var(--surface-page)' },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="lg" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={opened ? 'Закрыть меню' : 'Открыть меню'}
            />
            <Group gap={10} wrap="nowrap">
              <img src={BRAND.sunMark} alt="" width={26} height={26} />
              {/*
                На телефоне имя платформы иначе переносится на вторую строку и
                растягивает шапку: там оно мельче, но остаётся одной строкой.
              */}
              <Text
                fw={600}
                fz={{ base: 'sm', sm: 'lg' }}
                style={{
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                }}
              >
                {APP_NAME}
              </Text>
            </Group>
          </Group>
          <UserMenu />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2}>
            {items.map((item) => {
              const ItemIcon = item.icon;
              const active =
                location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
              const icon = <ItemIcon size={18} stroke={1.6} />;

              /*
                Compact mode drops the sub-menu: a flyout would be a second
                navigation pattern for one section. The parent becomes a plain
                link to its first area instead.
              */
              if (item.children && !compact) {
                return (
                  <NavLink
                    key={item.to}
                    label={item.label}
                    leftSection={icon}
                    defaultOpened={active}
                    childrenOffset={26}
                    styles={navLinkStyles(active)}
                  >
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        component={RouterNavLink}
                        to={child.to}
                        label={child.label}
                        active={(child.match ?? [child.to]).some((to) => isPathWithin(location.pathname, to))}
                        onClick={close}
                        variant="light"
                        styles={{ root: { borderRadius: 'var(--radius-control)' } }}
                      />
                    ))}
                  </NavLink>
                );
              }

              const to = item.children ? item.children[0]!.to : item.to;

              return (
                <Tooltip
                  key={item.to}
                  label={item.label}
                  position="right"
                  disabled={!compact}
                  openDelay={200}
                >
                  <NavLink
                    component={RouterNavLink}
                    to={to}
                    // Without a visible label the icon needs its own name (§58).
                    aria-label={item.label}
                    label={compact ? undefined : item.label}
                    active={active}
                    onClick={close}
                    leftSection={icon}
                    variant="light"
                    styles={navLinkStyles(active)}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        </AppShell.Section>

        <AppShell.Section>
          <Box
            px={compact ? 0 : 'xs'}
            py="sm"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <Tooltip
              label={`Поддержка · ${SUPPORT_EMAIL}`}
              position="right"
              disabled={!compact}
              openDelay={200}
            >
              <NavLink
                component="a"
                href={`mailto:${SUPPORT_EMAIL}`}
                aria-label={`Поддержка: ${SUPPORT_EMAIL}`}
                label={compact ? undefined : 'Поддержка'}
                description={compact ? undefined : SUPPORT_EMAIL}
                leftSection={<IconMail size={18} stroke={1.6} />}
                onClick={close}
                variant="subtle"
                styles={{
                  ...navLinkStyles(false),
                  description: {
                    color: 'var(--text-muted)',
                    fontSize: 'var(--mantine-font-size-xs)',
                  },
                }}
              />
            </Tooltip>
          </Box>
        </AppShell.Section>

        {/*
          Дизайн-системы в меню больше нет. Это техническая документация для
          разработчика — перечень интерфейсных образцов, — и администратору
          платформы она не нужна: Дарья видела в ней раздел продукта.

          Справочник никуда не делся: он лежит в репозитории отдельным файлом
          docs/design-system.html и открывается обычным браузером, без запуска
          приложения.
        */}
        <AppShell.Section>
          <Box px={compact ? 0 : 'xs'}>
            <Group justify={compact ? 'center' : 'flex-end'} visibleFrom="sm">
              <Tooltip
                label={compact ? 'Развернуть меню' : 'Свернуть меню'}
                position="right"
                openDelay={200}
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={compact ? 'input-sm' : 'md'}
                  w={compact ? '100%' : undefined}
                  radius="var(--radius-control)"
                  aria-label={compact ? 'Развернуть меню' : 'Свернуть меню'}
                  aria-expanded={!compact}
                  onClick={() => setCollapsed((value) => !value)}
                >
                  {/* The chevron points the way the panel will move. */}
                  {compact ? (
                    <IconChevronRight size={18} stroke={1.8} />
                  ) : (
                    <IconChevronLeft size={18} stroke={1.8} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {/*
          Новая страница открывается сверху, «Назад» возвращает прежнюю
          прокрутку. Без этого на телефоне партия, начатая кнопкой внизу
          страницы демо, открывалась прокрученной мимо панели хода.
        */}
        <ScrollRestoration />
        <Container size={wide ? SESSION_SURFACE_WIDTH : 'xl'} py="xl" px="lg">
          <Outlet />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
